import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { Customer } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/**
 * Contact details and what the shopper has spent. The backend sums paid orders in the store's base
 * currency, net of refunds, whatever currency each order was paid in.
 */
export function CustomerOverviewCard({ customer }: { customer: Customer }) {
  const { t } = useTranslation('customers');
  const format = useFormat();
  const none = (text: string) => <span className="text-muted-foreground">{text}</span>;

  return (
    <Card className="gap-3" data-tour="customers.detail.overview">
      <CardHeader>
        <CardTitle>{t('overview.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-3 sm:grid-cols-2" data-testid="customer-overview">
          <Row label={t('overview.orders')}>
            <span className="tabular-nums">{customer.ordersCount}</span>
          </Row>
          <Row label={t('overview.totalSpent')}>
            <span className="tabular-nums">{format.money(customer.totalSpent)}</span>
            <span className="text-muted-foreground block text-xs">
              {t('overview.totalSpentCurrency', { currency: customer.totalSpent.currency })}
            </span>
          </Row>
          <Row label={t('overview.lastOrder')}>
            {customer.lastOrderAt ? (
              <span title={format.utc(customer.lastOrderAt)}>
                {format.relative(customer.lastOrderAt)}
              </span>
            ) : (
              none(t('noOrders'))
            )}
          </Row>
          <Row label={t('overview.since')}>
            <span title={format.utc(customer.createdAt)}>{format.date(customer.createdAt)}</span>
          </Row>
          <Row label={t('fields.email')}>
            <a href={`mailto:${customer.email}`} className="break-all hover:underline">
              {customer.email}
            </a>
          </Row>
          <Row label={t('fields.phone')}>{customer.phone ?? none(t('overview.noPhone'))}</Row>
          <Row label={t('fields.acceptsMarketing')}>
            {customer.acceptsMarketing
              ? t('marketing.subscribed')
              : none(t('marketing.notSubscribed'))}
          </Row>
          <Row label={t('overview.account')}>
            {customer.hasPassword ? t('overview.hasPassword') : none(t('overview.noPassword'))}
          </Row>
          {customer.savedRingSize ? (
            <Row label={t('overview.ringSize')}>{customer.savedRingSize}</Row>
          ) : null}
        </dl>
      </CardContent>
    </Card>
  );
}
